import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import type { TFunction } from 'i18next';
import { Alert, Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError } from '@/api/errors';
import { importsApi, type ImportUpload } from '@/api/imports';
import { ChooseHowWeHelpButton } from '@/features/automation/ChooseHowWeHelpButton';
import { CurrentSettingsLine } from '@/features/automation/CurrentSettingsLine';
import { useAutomationGate } from '@/features/automation/useAutomationGate';
import { formatByteSize } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme/theme';
import { ImportProgress, type ImportProgressStep, type ImportStepState } from './ImportProgress';
import { IMPORT_MAX_BYTES, importFileProblem } from './importFile';

interface FileStepProps {
  accountId: number;
  accountName: string;
  onUploaded: (upload: ImportUpload, filename?: string) => void;
}

type FileStage = 'uploading' | 'reading' | 'ready';

interface Phase {
  kind: 'idle' | 'working' | 'failed';
  file: File | null;
  stage: FileStage;
  percent: number;
  note: string | null;
  message: string | null;
}

const IDLE: Phase = {
  kind: 'idle',
  file: null,
  stage: 'uploading',
  percent: 0,
  note: null,
  message: null,
};

/** Long enough for a test to see "Ready to review" before the wizard advances. */
const READY_MS = 500;

export function FileStep({ accountId, accountName, onUploaded }: FileStepProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const hintsId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const attemptRef = useRef(0);
  const mountedRef = useRef(true);
  const phaseRef = useRef<Phase>(IDLE);
  const [dragOver, setDragOver] = useState(false);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>(IDLE);
  const gate = useAutomationGate();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  function publish(next: Phase) {
    phaseRef.current = next;
    setPhase(next);
  }

  function messageFor(err: unknown): string {
    if (isApiError(err) && err.code === ErrorCodes.IMPORT_FILE_TOO_LARGE) {
      return t('imports.errorTooLarge');
    }
    if (isApiError(err) && err.code === ErrorCodes.IMPORT_UNPARSEABLE) {
      return t('imports.errorUnparseable');
    }
    if (isApiError(err) && err.code === ErrorCodes.PERMISSION_REQUIRED) {
      return t('imports.errorPermission');
    }
    return t('imports.errorGeneric');
  }

  async function run(file: File, note: string | null) {
    const attempt = ++attemptRef.current;
    setInvalid(null);
    const current = () => mountedRef.current && attempt === attemptRef.current;
    publish({ kind: 'working', file, stage: 'uploading', percent: 0, note, message: null });
    let failedStage: 'uploading' | 'reading' = 'uploading';
    try {
      // One request (design §8): the upload's response carries the proposed mapping. Once every
      // byte is sent the server is reading the file, until that response arrives.
      const uploaded = await importsApi.upload(accountId, file, (event) => {
        if (!current()) return;
        const total = event.total ?? 0;
        if (total <= 0) return;
        if (event.loaded >= total) {
          const now = phaseRef.current;
          if (now.kind !== 'working' || now.stage !== 'uploading') return;
          failedStage = 'reading';
          publish({ kind: 'working', file, stage: 'reading', percent: 100, note, message: null });
          return;
        }
        const percent = Math.min(100, Math.round((event.loaded / total) * 100));
        setPhase((prev) => {
          // A progress update queued before failure must not write the ref back to "working":
          // the state is already failed, and a stale ref would ignore the next file.
          if (!current()) return prev;
          if (phaseRef.current.kind !== 'working' || phaseRef.current.stage !== 'uploading') {
            return prev;
          }
          if (prev.kind !== 'working' || prev.stage !== 'uploading') return prev;
          const next = { ...prev, percent };
          phaseRef.current = next;
          return next;
        });
      });
      if (!current()) return;
      publish({ kind: 'working', file, stage: 'ready', percent: 100, note, message: null });
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, READY_MS);
      });
      if (!current()) return;
      onUploaded(uploaded, file.name);
    } catch (err) {
      if (!current()) return;
      publish({
        kind: 'failed',
        file,
        stage: failedStage,
        percent: 0,
        note,
        message: messageFor(err),
      });
    }
  }

  function acceptFiles(list: FileList | File[] | null | undefined) {
    if (phaseRef.current.kind === 'working') return;
    const files = list === null || list === undefined ? [] : Array.from(list);
    const file = files[0];
    if (file === undefined) return;
    const note = files.length > 1 ? t('imports.onlyOne', { name: file.name }) : null;
    const problem = importFileProblem(file);
    if (problem === 'type') {
      setInvalid(t('imports.errorWrongType'));
      publish({ ...IDLE, note });
      return;
    }
    if (problem === 'size') {
      setInvalid(t('imports.errorTooLarge'));
      publish({ ...IDLE, note });
      return;
    }
    void run(file, note);
  }

  function openPicker() {
    inputRef.current?.click();
  }

  function chooseAnother() {
    attemptRef.current += 1;
    dragDepth.current = 0;
    setDragOver(false);
    setInvalid(null);
    publish(IDLE);
    openPicker();
  }

  function onDragEnter(event: DragEvent) {
    event.preventDefault();
    dragDepth.current += 1;
    if (dragDepth.current === 1) setDragOver(true);
  }

  function onDragLeave(event: DragEvent) {
    event.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragOver(false);
    }
  }

  if (gate.loading) {
    return <Typography variant="body2">{t('common.loading')}</Typography>;
  }

  if (gate.needsConsent) {
    return (
      <Stack spacing={2}>
        <Typography variant="body1">{t('consent.body')}</Typography>
        <ChooseHowWeHelpButton
          catalog={gate.catalog}
          onCatalogChange={() => void gate.reload()}
          onCompleted={() => void gate.reload()}
        />
      </Stack>
    );
  }

  const prompt = dragOver ? t('imports.releaseToUpload') : t('imports.dropPrompt');
  const showProgress = phase.kind !== 'idle' && phase.file !== null;
  const announcement = announcementFor(t, phase);

  return (
    <Stack spacing={2}>
      <Typography variant="body1">{t('imports.uploadingTo', { name: accountName })}</Typography>
      {gate.catalog !== null && (
        <CurrentSettingsLine catalog={gate.catalog} onCatalogChange={() => void gate.reload()} />
      )}
      {invalid !== null && <Alert severity="error">{invalid}</Alert>}
      {phase.kind === 'failed' && phase.message !== null && (
        <Alert severity="error">{phase.message}</Alert>
      )}
      {showProgress && phase.file !== null ? (
        <Stack spacing={1}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {phase.file.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {formatByteSize(phase.file.size, locale)}
          </Typography>
          {phase.note !== null && <Typography variant="body2">{phase.note}</Typography>}
          <ImportProgress
            title={announcement}
            announcement={announcement}
            steps={fileSteps(t, phase)}
            bar={phase.stage === 'reading' ? 'indeterminate' : 'determinate'}
            percent={phase.stage === 'uploading' ? phase.percent : 100}
          />
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
            {phase.kind === 'failed' && phase.file !== null && (
              <Button
                variant="contained"
                onClick={() => {
                  if (phase.file !== null) void run(phase.file, phase.note);
                }}
              >
                {t('imports.tryAgain')}
              </Button>
            )}
            <Button onClick={chooseAnother}>{t('imports.chooseAnother')}</Button>
          </Stack>
        </Stack>
      ) : (
        <Box
          role="button"
          tabIndex={0}
          aria-label={prompt}
          // The size limit, the file types and any note are the zone's description (B10).
          aria-describedby={
            phase.note !== null
              ? `${hintsId}-size ${hintsId}-types ${hintsId}-note`
              : `${hintsId}-size ${hintsId}-types`
          }
          onClick={openPicker}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              openPicker();
            }
          }}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(event) => {
            event.preventDefault();
            dragDepth.current = 0;
            setDragOver(false);
            acceptFiles(event.dataTransfer.files);
          }}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? tokens.brandBoundary : tokens.line,
            borderRadius: `${tokens.radius.control}px`,
            bgcolor: dragOver ? tokens.hover : 'transparent',
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            '&:focus-visible': {
              outline: `2px solid ${tokens.focusRing}`,
              outlineOffset: '2px',
            },
          }}
        >
          <Typography variant="body1">{prompt}</Typography>
          <Typography
            id={`${hintsId}-size`}
            variant="body2"
            sx={{ color: 'text.secondary', mt: 1 }}
          >
            {t('imports.maxSize', { size: formatByteSize(IMPORT_MAX_BYTES, locale) })}
          </Typography>
          <Typography id={`${hintsId}-types`} variant="body2" sx={{ color: 'text.secondary' }}>
            {t('imports.acceptedTypes')}
          </Typography>
          {phase.note !== null && (
            <Typography id={`${hintsId}-note`} variant="body2" sx={{ mt: 1 }}>
              {phase.note}
            </Typography>
          )}
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              mt: 2,
              px: 2,
              py: 0.75,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: `${tokens.radius.control}px`,
              fontWeight: 500,
              // A button's look, in sentence case (contract v2 §7, TMMAPP-0154 §A 3).
            }}
          >
            {t('imports.chooseFile')}
          </Box>
        </Box>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.txt,.tsv"
        hidden
        onChange={(event) => {
          acceptFiles(event.target.files);
          event.target.value = '';
        }}
      />
    </Stack>
  );
}

function announcementFor(t: TFunction, phase: Phase) {
  if (phase.file === null || phase.kind === 'idle') return '';
  if (phase.stage === 'uploading') return t('imports.uploadingFile', { name: phase.file.name });
  if (phase.stage === 'reading') return t('imports.readingFile');
  return t('imports.readyToReview');
}

function fileSteps(t: TFunction, phase: Phase): ImportProgressStep[] {
  const fileName = phase.file?.name ?? '';
  const order: FileStage[] = ['uploading', 'reading', 'ready'];
  const labels: Record<FileStage, string> = {
    uploading: t('imports.uploadingFile', { name: fileName }),
    reading: t('imports.readingFile'),
    ready: t('imports.readyToReview'),
  };
  const active = order.indexOf(phase.stage);
  return order.map((id, index) => {
    let state: ImportStepState = 'pending';
    if (phase.kind === 'failed' && index === active) state = 'failed';
    else if (index < active || phase.stage === 'ready') state = 'done';
    else if (index === active) state = 'active';
    return { label: labels[id], state };
  });
}
